import { assertNever } from "@redotech/util/type";
import {
  ActiveElement,
  ArcElement,
  Chart,
  ChartData,
  ChartEvent,
  ChartOptions,
  Tooltip as ChartTooltip,
} from "chart.js";
import classNames from "classnames";
import { memo, useCallback, useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { getVisualizationColors } from "../../../theme/colors";
import { Tooltip } from "../../tooltip/tooltip";
import * as redoPieChartCss from "./redo-pie-chart.module.css";

Chart.register(ArcElement);
Chart.register(ChartTooltip);

// Copied from line-chart.tsx, feel free to change
const FONT = 'Inter, "Helvetica Neue", Arial, sans-serif';

export interface RedoPieChartSeriesDatum {
  label: string;
  value: number;
  color?: string;
}

export const redoPieChartHoleSize = ["0%", "25%", "50%", "75%"] as const;
export type RedoPieChartHoleSize = (typeof redoPieChartHoleSize)[number];

export const redoPieChartSize = [
  "xxs",
  "xs",
  "sm",
  "md",
  "lg",
  "responsive",
] as const;
export type RedoPieChartSize = (typeof redoPieChartSize)[number];

export const redoPieChartLabelPosition = ["below", "right"] as const;
export type RedoPieChartLabelPosition =
  (typeof redoPieChartLabelPosition)[number];

export const redoPieChartSort = [
  "label-ascending",
  "value-descending",
  "none",
] as const;
export type RedoPieChartSort = (typeof redoPieChartSort)[number];

export const RedoPieChart = memo(function RedoPieChart({
  series,
  size,
  holeSize = "50%",
  sort = "value-descending",
  onClick,
  labelPosition = "below",
  centerText,
  displayLabelValues = false,
  showLegend = true,
  valueFormatter = (value: number) => value.toString(),
  "data-dd-action-name": actionName,
}: {
  "data-dd-action-name"?: string;
  series: RedoPieChartSeriesDatum[];
  size: RedoPieChartSize;
  holeSize?: RedoPieChartHoleSize;
  sort?: RedoPieChartSort;
  onClick?: (label: string) => void;
  labelPosition?: RedoPieChartLabelPosition;
  centerText?: { title: string; subtitle?: string };
  displayLabelValues?: boolean;
  showLegend?: boolean;
  valueFormatter?: (value: number) => string;
}) {
  const sortedSeriesWithColors = useMemo(() => {
    // sort series
    const sortedSeries = [...series];
    switch (sort) {
      case "label-ascending":
        sortedSeries.sort((a, b) => a.label.localeCompare(b.label));
        break;
      case "value-descending":
        sortedSeries.sort((a, b) => b.value - a.value);
        break;
      case "none":
        break;
      default:
        assertNever(sort);
    }

    // add colors to sorted series (use provided color or fall back to auto-generated)
    const colors = getVisualizationColors(series.length);
    const _sortedSeriesWithColors = sortedSeries.map((datum, index) => ({
      ...datum,
      color: datum.color ?? colors[index],
    }));

    return _sortedSeriesWithColors;
  }, [series, sort]);

  const data: ChartData<"doughnut"> = useMemo(() => {
    return {
      labels: sortedSeriesWithColors.map((datum) => datum.label),
      datasets: [
        {
          data: sortedSeriesWithColors.map((datum) => datum.value),
          backgroundColor: sortedSeriesWithColors.map((datum) => datum.color),
          borderWidth: 0,
          cutout: holeSize,
        },
      ],
    };
  }, [sortedSeriesWithColors, holeSize]);

  const _onClick = useCallback(
    (event: ChartEvent, elements: ActiveElement[]) => {
      if (
        onClick &&
        elements.length > 0 &&
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        elements[0].index < sortedSeriesWithColors.length
      ) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        const clickedLabel = sortedSeriesWithColors[elements[0].index].label;
        onClick(clickedLabel);
      }
    },
    [onClick, sortedSeriesWithColors],
  );

  const options: ChartOptions<"doughnut"> = useMemo(() => {
    return {
      onClick: _onClick,
      plugins: {
        tooltip: {
          enabled: false,
          callbacks: {
            label: (context) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              const value = context.raw as number;
              return valueFormatter(value);
            },
          },
          external: (context) => {
            const { chart, tooltip } = context;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            let tooltipEl = chart.canvas.parentNode?.querySelector(
              ".chartjs-tooltip",
            ) as HTMLDivElement | null;

            if (!tooltipEl) {
              tooltipEl = document.createElement("div");
              tooltipEl.classList.add("chartjs-tooltip");
              Object.assign(tooltipEl.style, {
                position: "absolute",
                pointerEvents: "none",
                zIndex: "2147483006",
                transition:
                  "opacity 0.25s ease, left 0.25s ease, top 0.25s ease",
                opacity: "0",
              });
              chart.canvas.parentNode?.append(tooltipEl);
              chart.canvas.addEventListener("mouseleave", () => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                tooltipEl!.style.opacity = "0";
              });
            }

            if (tooltip.opacity === 0) {
              tooltipEl.style.opacity = "0";
              return;
            }

            const titleLines = tooltip.title ?? [];
            const bodyLines = tooltip.body?.flatMap((b) => b.lines) ?? [];
            tooltipEl.innerHTML = "";
            const inner = document.createElement("div");
            Object.assign(inner.style, {
              backgroundColor: "#000000",
              color: "#ffffff",
              borderRadius: "var(--border-radius-sm)",
              padding: "8px",
              fontFamily: FONT,
              fontSize: "12px",
              textAlign: "center",
              whiteSpace: "nowrap",
            });
            for (const [text, bold] of [
              ...titleLines.map((t) => [t, true] as const),
              ...bodyLines.map((b) => [b, false] as const),
            ]) {
              const div = document.createElement("div");
              if (bold) {
                div.style.fontWeight = "bold";
              }
              div.textContent = String(text);
              inner.append(div);
            }
            tooltipEl.append(inner);

            Object.assign(tooltipEl.style, {
              opacity: "1",
              left: tooltip.caretX + "px",
              top: tooltip.caretY + "px",
              transform: "translate(-50%, -100%)",
            });
          },
        },
        legend: { display: false },
      },
    };
  }, [_onClick, valueFormatter]);

  return (
    <Flex
      className={classNames(
        redoPieChartCss.container,
        labelPosition === "right" ? redoPieChartCss[size] : "",
      )}
      data-arbiter="RedoPieChart"
      data-dd-action-name={actionName}
      dir={labelPosition === "below" ? "column" : "row"}
      gap={labelPosition === "below" ? "5xl" : "xl"}
    >
      <Flex align="center" dir="column" justify="center">
        <DoughnutWithText
          centerText={centerText}
          data={data}
          options={options}
          size={size}
        />
      </Flex>
      {showLegend && (
        <Label
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          data={sortedSeriesWithColors}
          displayValues={displayLabelValues}
          position={labelPosition}
          valueFormatter={valueFormatter}
        />
      )}
    </Flex>
  );
});

const DoughnutWithText = ({
  data,
  options,
  size,
  centerText,
}: {
  data: ChartData<"doughnut">;
  options: ChartOptions<"doughnut">;
  size: RedoPieChartSize;
  centerText?: { title: string; subtitle?: string };
}) => {
  return (
    <Flex
      className={classNames(
        redoPieChartCss.doughnutContainer,
        redoPieChartCss[size],
      )}
      style={{ position: "relative" }}
    >
      {centerText && (
        <Flex
          align="center"
          className={redoPieChartCss.centerText}
          dir="column"
          gap="none"
          justify="center"
        >
          {centerText.title && (
            <Text fontSize="xl" fontWeight="bold" textColor="primary">
              {centerText.title}
            </Text>
          )}
          {centerText.subtitle && (
            <Text fontSize="sm" textColor="secondary">
              {centerText.subtitle}
            </Text>
          )}
        </Flex>
      )}
      <Doughnut data={data} options={options} />
    </Flex>
  );
};

const Label = ({
  position,
  data,
  displayValues,
  valueFormatter,
}: {
  position: RedoPieChartLabelPosition;
  data: { label: string; value: number; color: string }[];
  displayValues: boolean;
  valueFormatter: (value: number) => string;
}) => {
  return (
    <Flex
      className={classNames(
        redoPieChartCss.legend,
        position === "right" ? redoPieChartCss.maxWidth : "",
      )}
      dir="column"
      gap={position === "below" ? "xl" : "xs"}
    >
      {data.map((datum, index) => (
        <Flex gap="md" key={index}>
          <Flex
            className={redoPieChartCss.legendItemColorDisplay}
            style={{ backgroundColor: datum.color }}
          />
          {displayValues ? (
            <>
              <Text
                className={redoPieChartCss.legendItemValue}
                fontSize="sm"
                fontWeight="bold"
              >
                {valueFormatter(datum.value)}
              </Text>
              <Text
                className={redoPieChartCss.legendItemLabel}
                fontSize="sm"
                fontWeight="regular"
                textColor="secondary"
              >
                {datum.label}
              </Text>
            </>
          ) : (
            <Tooltip subtitle={valueFormatter(datum.value)} title={datum.label}>
              <div>
                <Text
                  className={redoPieChartCss.legendItemLabel}
                  fontSize="sm"
                  fontWeight="regular"
                  textColor="secondary"
                >
                  {datum.label}
                </Text>
              </div>
            </Tooltip>
          )}
        </Flex>
      ))}
    </Flex>
  );
};
